/** @jsx jsx */
import { useState, useEffect, useRef } from 'react'
import { css, jsx, keyframes } from '@emotion/core'
import useMedia from 'use-media'
import createMomentsSDK from '@livechat/moments-sdk'

import { Button } from '@livechat/design-system'
import '@livechat/design-system/dist/design-system.css'

import api from '../api'
import { checkLocalStorage, sendPostMessage } from '../utils'

import {
  TextAnswer,
  SingleChoiceAnswer,
  MultiChoiceAnswer,
  Score,
  Sent,
  PublicSurveySkeleton,
  Heading,
} from '../components/PublicSurvey'

import MarkdownRenderer from '../components/MarkdownRenderer'

const showAnimation = keyframes`
 from {
  transform: scale(0.5) translateY(20px);
  opacity: 0.7;
 }
`

const containerCss = (theme) => css`
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background-color: ${theme.background || '#f8f8fa'};
  line-height: 22px;

  ${theme.color &&
  css`
    color: ${theme.color};
  `}

  .heading {
    width: 100%;
    height: 30px;
    max-width: 430px;
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    font-size: 16px;
    background-color: ${theme.container_background || 'white'};
    box-shadow: ${theme.container_box_shadow ||
    'rgb(228 228 228) 0px 1px 5px 0px'};
    padding: 10px 20px;
    border-radius: 10px;

    > a {
      width: 30px;
      height: 30px;
      margin-right: 10px;
    }
  }

  .surveys-logo {
    width: 30px;
    height: 100%;
  }
`

const appCss = (theme) => css`
  width: 100%;
  height: 100%;
  max-width: 450px;
  background-color: ${theme.container_background || 'white'};
  box-shadow: ${theme.container_box_shadow ||
  'rgb(228 228 228) 0px 1px 5px 0px'};
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  @media (min-width: 400px) {
    height: calc(80% - 100px);
    padding: 0 10px;
    border-radius: 10px;
  }

  .lc-spinner {
    margin-bottom: 100px;
  }

  .survey {
    height: 100%;
    width: 100%;
  }

  .fields {
    height: calc(100% - 77px);

    ${theme.footer?.padding &&
    css`
      height: calc(100% - ${77 + theme.footer?.padding}px);
    `}

    overflow-y: scroll;
    margin-bottom: 10px;
    margin: 0 10px;
    padding-top: 20px;
    ${theme.fields?.paddingLeft &&
    css`
      padding-left: ${theme.fields?.paddingLeft}px;
    `}
    ${theme.fields?.paddingRight &&
    css`
      padding-right: ${theme.fields?.paddingRight}px;
    `}

    ::-webkit-scrollbar {
      display: none;
    }
  }

  .field {
    margin-bottom: 20px;
  }

  .question {
    font-size: 15px;
    font-weight: 400;
    margin-bottom: 10px;
    word-break: break-word;
    ${theme.question_color &&
    css`
      color: ${theme.question_color};
    `}

    @media (min-width: 400px) {
      font-size: 16px;
      margin-bottom: 10px;
    }
  }

  .error {
    font-size: 12px;
    color: #d64646;
    margin: 5px 0;
  }

  .answer {
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;

    .lc-input {
      ${theme.input?.color &&
      css`
        color: ${theme.input.color};
      `}

      ${theme.input?.background_color &&
      css`
        background-color: ${theme.input.background_color};
      `}

      ${theme.input?.border_color &&
      css`
        border-color: ${theme.input.border_color};
      `}

      ::placeholder {
        ${theme.input?.placeholder_color &&
        css`
          color: ${theme.input.placeholder_color};
        `}
      }
    }

    .lc-radio__circle {
      ${theme.score?.background_color &&
      css`
        background-color: ${theme.score.background_color};
      `}

      ${theme.score?.border_color &&
      css`
        border-color: ${theme.score.border_color};
      `}
    }

    .lc-radio__text {
      ${theme.score?.color &&
      css`
        color: ${theme.score.color};
      `}
    }

    .lc-radio {
      margin-bottom: 5px;
    }

    .lc-checkbox {
      margin-bottom: 5px;
    }

    .lc-checkbox__text {
      ${theme.checkbox?.color &&
      css`
        color: ${theme.checkbox.color};
      `}
    }

    .lc-checkbox__square {
      ${theme.checkbox?.background_color &&
      css`
        background-color: ${theme.checkbox.background_color};
      `}
    }
  }

  .footer {
    display: flex;
    justify-content: flex-end;
    border-top: 1px solid ${theme.divider_color || 'rgb(227, 227, 227)'};
    padding: 10px;

    ${theme.footer?.padding &&
    css`
      padding: ${theme.footer?.padding}px;
    `}

    .lc-btn {
      ${theme.submit_background_color &&
      css`
        background-color: ${theme.submit_background_color};
      `}

      ${theme.submit_border_color &&
      css`
        border-color: ${theme.submit_border_color};
      `}

      ${theme.submit_color &&
      css`
        color: ${theme.submit_color};
      `}
    }
  }

  .divider {
    width: 100%;
    height: 1px;
    background-color: ${theme.divider_color || 'rgb(227, 227, 227)'};
    margin-bottom: 20px;
  }

  .lc-checkbox__text {
    max-width: 90%;
  }

  .lc-radio__text {
    max-width: 90%;
  }
`

const sentCss = css`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 450px;
  background-color: white;
  box-shadow: rgb(228 228 228) 0px 1px 5px 0px;

  @media (min-width: 400px) {
    height: calc(80% - 100px);
    padding: 0 10px;
    border-radius: 10px;
  }

  .thank-you {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 80px;
  }

  .text {
    text-align: center;
  }

  .main-text {
    display: inline-block;
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 3px;
  }

  .icon {
    margin-bottom: 5px;
    width: 70px;
    animation: ${showAnimation} 0.3s ease;
  }
`

const App = () => {
  const [response, setResponse] = useState({})
  const [error, setError] = useState([])
  const [lengthError, setLengthError] = useState([])
  const [loading, setLoading] = useState(false)
  const [view, setView] = useState()
  const isWide = useMedia({ minWidth: 400 })

  const refMomentsSDK = useRef(null)

  const [publicToken, setPublicToken] = useState()
  const [survey, setSurvey] = useState({})
  const [isSurveyInactive, setIsSurveyInactive] = useState(false)

  const [ipAdress, setIpAdress] = useState()
  const [paramsField, setParamsField] = useState()

  const momentsSdkExists = !!refMomentsSDK.current
  const isLoading = survey.structure ? !survey.structure.length : true

  const disableFragile = async () => {
    if (momentsSdkExists) {
      await refMomentsSDK.current.setIsFragile(false)
    }
  }

  useEffect(() => {
    const request = async () => {
      try {
        const response = await api.getIPAddress()
        setIpAdress(response)
      } catch (err) {
        console.log(err)
      }
    }

    if (survey.collectAddress) {
      request()
    }
  }, [survey])

  useEffect(() => {
    const request = async () => {
      createMomentsSDK({
        icon: `${process.env.REACT_APP_SURVEYS}/survey_icon.ico`,
        title: survey.name,
        isFragile: true,
      })
        .then((momentsSDK) => {
          refMomentsSDK.current = momentsSDK
        })
        .catch((err) => {
          console.log('err', err)
        })
    }

    if (survey.name) {
      request()
    }
  }, [survey.name])

  useEffect(() => {
    try {
      const href = window.location.href
      const public_token = href.split('/')[href.split('/').length - 1]

      if (survey?.paramsField?.length > 0) {
        const queryString = window.location.search
        const urlParams = new URLSearchParams(queryString)

        const fields = {}

        survey.paramsField.forEach((paramField) => {
          const fieldValue = urlParams.get(paramField)

          if (fieldValue) {
            fields[paramField] = fieldValue
          }
        })

        setParamsField(fields)
      }

      setPublicToken(public_token)
    } catch (err) {
      console.log('URLSearchParams')
      console.error(err)
    }
  }, [survey])

  useEffect(() => {
    const request = async () => {
      try {
        setLoading(true)
        const response = await api.getPublicSurvey(publicToken)
        setLoading(false)

        if (response === 'SURVEY_INACTIVE') {
          setIsSurveyInactive(true)
        } else {
          setSurvey(JSON.parse(response))
          sendPostMessage('SURVEY_LOADED')
        }
      } catch (err) {
        console.error(err)
      }
    }

    if (publicToken) {
      request()
    }
  }, [publicToken])

  useEffect(() => {
    const initialResponse = {}
    survey.structure &&
      survey.structure.forEach((el, i) => {
        if (el.type === 'TEXT_ANSWER') {
          initialResponse[i] = undefined
        }
        if (el.type === 'SINGLE_CHOICE') {
          initialResponse[i] = undefined
        }
        if (el.type === 'MULTI_CHOICE') {
          initialResponse[i] = []
        }
      })

    setResponse(initialResponse)
  }, [survey])

  const validate = () => {
    const currErrors = []
    const lengthErrors = []

    survey.structure &&
      survey.structure.forEach((el, i) => {
        if (el.required) {
          if (el.type === 'MULTI_CHOICE' && !response[i].length) {
            currErrors.push(i)
          } else if (response[i] === undefined) {
            currErrors.push(i)
          } else if (el.type === 'TEXT_ANSWER' && response[i].length > 1500) {
            lengthErrors.push(i)
          }
        }
      })

    setError(currErrors)
    setLengthError(lengthErrors)

    return {
      errors: currErrors,
      lengthErrors,
    }
  }

  const handleSubmit = async () => {
    const { errors: currErrors, lengthErrors } = validate()

    if (currErrors.length === 0 && lengthErrors.length === 0) {
      try {
        setLoading(true)
        const status = await api.sendPublicResponse(
          publicToken,
          response,
          ipAdress,
          paramsField
        )
        setLoading(false)

        if (status === 'OK') {
          if (checkLocalStorage() && !survey.allowMultipleResponses) {
            localStorage.setItem(publicToken, 'sent')
          }
          setView('SENT')
          sendPostMessage('SURVEY_COMPLETED')
        } else {
          setView('SENT')
        }

        if (survey?.externalService?.url) {
          try {
            await api.sendResponseToExternalService(
              response,
              survey.name,
              survey?.externalService?.url
            )
          } catch (e) {}
        }

        if (momentsSdkExists) {
          if (survey.isSettingAttributes) {
            const attributes = {}

            try {
              survey.structure.forEach((field, i) => {
                const currResp = response[i]
                const currType = field.type

                if (currType === 'TEXT_ANSWER') {
                  attributes[i] = currResp || ''
                }
                if (currType === 'MULTI_CHOICE') {
                  attributes[i] = currResp
                    ?.map((el) => field.answers[el])
                    ?.join(', ')
                }
                if (currType === 'SINGLE_CHOICE') {
                  attributes[i] = field?.answers[currResp] || ''
                }
                if (currType === 'SCORE') {
                  attributes[i] = currResp
                }
              })
            } catch (err) {
              console.error(err)
            }

            Object.keys(survey?.attributes).forEach(async (attr) => {
              const attrKey = survey?.attributes[attr]
              try {
                await refMomentsSDK.current.setAttributes({
                  [attrKey]: `${attributes[attr]}`,
                })
              } catch (err) {
                console.log('err', err)
              }
            })
          }

          await refMomentsSDK.current.sendMessage({
            text:
              survey?.staticContent?.confirmation_message ||
              'The survey has been completed!',
          })
        }

        setTimeout(() => {
          if (momentsSdkExists) {
            refMomentsSDK.current.close()
          }
        }, 3000)
      } catch (err) {
        console.log('refMomentsSDK')
        console.error(err)
      }
    } else {
      const questionOffset = document
        .getElementById(`question-${currErrors[0]}`)
        .getBoundingClientRect().top

      const fields = document.getElementById('fields')

      if (fields.scrollTop < questionOffset) {
        fields.scroll({
          top: questionOffset - 220,
          behavior: 'smooth',
        })
      }
    }
  }

  if (isSurveyInactive) {
    disableFragile()

    return (
      <div css={containerCss}>
        {isWide && (
          <Heading
            isLoading={isLoading}
            options={{ title: 'Survey is inactive' }}
          />
        )}

        <div css={sentCss}>
          <div className="thank-you">
            <div className="text">
              <span className="main-text">
                This survey needs to be activated first.
              </span>
              <br />
              Please contact the license owner.
            </div>
          </div>
        </div>
      </div>
    )
  }

  const { theme = {}, staticContent = {} } = survey

  const wasSurveyCompleted = survey?.settings?.allowMultipleSurveyResponses
    ? false
    : checkLocalStorage() && localStorage.getItem(publicToken) === 'sent'

  if (view === 'SENT' || wasSurveyCompleted) {
    disableFragile()
    return (
      <Sent
        isWide={isWide}
        isLoading={isLoading}
        survey={survey}
        theme={theme}
      />
    )
  }

  return (
    <div css={containerCss(theme)}>
      {isWide && <Heading isLoading={isLoading} survey={survey} />}
      <div css={appCss(theme)}>
        {!isLoading ? (
          <div className="survey">
            <div className="fields" id="fields">
              {survey.structure?.map((el, index) => {
                return (
                  <div key={`field-${index}`}>
                    <div className="field">
                      <div className="question" id={`question-${index}`}>
                        {survey.structure.length !== 1 ? `${index + 1}.` : ''}{' '}
                        <MarkdownRenderer>{el.question}</MarkdownRenderer>
                      </div>
                      <div className="answer">
                        {el.type === 'TEXT_ANSWER' && (
                          <TextAnswer
                            index={index}
                            response={response}
                            setResponse={setResponse}
                          />
                        )}
                        {el.type === 'SINGLE_CHOICE' && (
                          <SingleChoiceAnswer
                            field={el}
                            response={response}
                            setResponse={setResponse}
                            index={index}
                          />
                        )}
                        {el.type === 'MULTI_CHOICE' && (
                          <MultiChoiceAnswer
                            field={el}
                            response={response}
                            setResponse={setResponse}
                            index={index}
                          />
                        )}
                        {el.type === 'SCORE' && (
                          <Score
                            field={el}
                            fieldIndex={index}
                            response={response}
                            setResponse={setResponse}
                            theme={theme}
                          />
                        )}
                        {error.includes(index) && (
                          <div className="error">
                            Please answer this question.
                          </div>
                        )}
                        {lengthError.includes(index) && (
                          <div className="error">This answer is too long.</div>
                        )}
                      </div>
                    </div>
                    {index < survey.structure.length - 1 && (
                      <div className="divider" />
                    )}
                  </div>
                )
              })}
            </div>
            <div className="footer">
              <Button primary onClick={handleSubmit} loading={loading}>
                {staticContent?.send_response_button || 'Send response'}
              </Button>
            </div>
          </div>
        ) : (
          <PublicSurveySkeleton />
        )}
      </div>
    </div>
  )
}

export default App
